"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
/**
 * Вычисляет значение CSS-переменной, включая выражения calc
 *
 * @param varName - Имя CSS-переменной (например, '--top-fixed')
 *
 * @returns Вычисленное значение в пикселях
 */
function getComputedCSSVariable(varName) {
    // Создаем временный элемент
    const tempElement = document.createElement('div');
    // Применяем переменную как свойство, чтобы браузер вычислил значение
    tempElement.style.position = 'absolute';
    tempElement.style.visibility = 'hidden';
    tempElement.style.top = `var(${varName})`;
    // Добавляем элемент в DOM
    document.body.appendChild(tempElement);
    // Получаем вычисленное значение
    const computedValue = parseFloat(window.getComputedStyle(tempElement).top) || 0;
    // Удаляем временный элемент
    document.body.removeChild(tempElement);
    return computedValue;
}
/**
 * Инициализирует двустороннюю фиксацию элемента при скролле
 *
 * @param container - Контейнер для фиксируемого элемента
 * @param stickyElement - Элемент, который будет фиксироваться
 * @param contentBoundary - Элемент, определяющий нижнюю границу контента
 * @param options - Настройки фиксации
 */
const initStickyScroll = (container, stickyElement, contentBoundary, options = {}) => {
    let lastScrollPosition = null;
    let scrollOffset = 0;
    let topFixedPosition = calculateTopPosition(options);
    const bottomFixedPosition = 0;
    // Вычисление верхней позиции с учетом настроек
    function calculateTopPosition(options) {
        const rootTopFixed = getComputedCSSVariable('--top-fixed') || 0;
        return rootTopFixed + (options.extraTopPadding || 0);
    }
    // Обновление верхней позиции при изменении
    const refreshTopPosition = () => {
        topFixedPosition = calculateTopPosition(options);
    };
    // Основной обработчик скролла
    const handleScroll = () => {
        const containerRect = container.getBoundingClientRect();
        const contentBottom = contentBoundary.getBoundingClientRect().bottom;
        const elementRect = stickyElement.getBoundingClientRect();
        const viewportHeight = document.documentElement.clientHeight;
        // Проверка на достижение конца контента
        if (containerRect.bottom >= contentBottom)
            return;
        const elementBottomPosition = containerRect.top + elementRect.height;
        const distanceToContentBottom = Math.round(elementBottomPosition - contentBottom);
        const distanceToViewportBottom = Math.round(elementBottomPosition - viewportHeight);
        if (elementRect.height > viewportHeight) {
            handleLargeElementScroll(containerRect, elementRect, distanceToContentBottom, distanceToViewportBottom, viewportHeight);
        }
        else {
            handleSmallElementScroll(containerRect, distanceToContentBottom);
        }
        lastScrollPosition = containerRect.top;
    };
    // Обработка больших элементов (выше viewport)
    function handleLargeElementScroll(containerRect, elementRect, distanceToContentBottom, distanceToViewportBottom, viewportHeight) {
        const isScrollingDown = lastScrollPosition !== null && containerRect.top < lastScrollPosition;
        if (isScrollingDown) {
            if (distanceToViewportBottom + bottomFixedPosition > distanceToContentBottom) {
                if (elementRect.bottom - viewportHeight + bottomFixedPosition <= 0) {
                    applyPosition(viewportHeight - elementRect.height - bottomFixedPosition, 'is-fixed');
                    scrollOffset = bottomFixedPosition + containerRect.top + elementRect.height - viewportHeight;
                }
                else {
                    applyPosition(-scrollOffset, 'is-aligned');
                }
            }
            else {
                applyPosition(-distanceToContentBottom, 'is-aligned');
                scrollOffset = distanceToContentBottom;
            }
        }
        else {
            if (containerRect.top - topFixedPosition < 0) {
                if (elementRect.top - topFixedPosition >= 0) {
                    applyPosition(topFixedPosition, 'is-fixed');
                    scrollOffset = containerRect.top - topFixedPosition;
                }
                else {
                    applyPosition(-scrollOffset, 'is-aligned');
                }
            }
            else {
                resetPosition();
                scrollOffset = 0;
            }
        }
    }
    // Обработка маленьких элементов (меньше viewport)
    function handleSmallElementScroll(containerRect, distanceToContentBottom) {
        if (containerRect.top - topFixedPosition <= 0) {
            if (containerRect.top - topFixedPosition <= distanceToContentBottom) {
                applyPosition(-distanceToContentBottom, 'is-aligned');
            }
            else {
                applyPosition(topFixedPosition, 'is-fixed');
            }
        }
        else {
            resetPosition();
        }
    }
    // Применение позиционирования
    function applyPosition(topValue, positionState) {
        stickyElement.classList.remove('is-fixed', 'is-aligned');
        stickyElement.classList.add(positionState);
        stickyElement.style.position = positionState === 'is-fixed' ? 'fixed' : 'relative';
        stickyElement.style.top = `${topValue}px`;
    }
    // Сброс позиционирования
    function resetPosition() {
        stickyElement.classList.remove('is-fixed', 'is-aligned');
        stickyElement.style.position = '';
        stickyElement.style.top = '';
    }
    // Подписка на события
    window.addEventListener('top-fixed-changed', refreshTopPosition, false);
    window.addEventListener('scroll', handleScroll, { passive: true });
    document.body.addEventListener('scroll', handleScroll, { passive: true });
};
exports.default = initStickyScroll;
